import React, { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import star1 from "../../assets/landing/star_1.svg";
import star2 from "../../assets/landing/star_2.svg";

export default function PromoModal({
  open,
  onClose,
  translations = {},
  couponValue,
  couponCode,
  couponDescription,
  language
}) {
  const t = translations?.promo || {};
  const closeBtnRef = useRef(null);

  //  star layer root
  const starRef = useRef(null);
  const panelRef = useRef(null); //  new: anchor to the .group panel

  // Lock body scroll without triggering full-page layout reflow
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  // Preload & decode star SVGs once
  useEffect(() => {
    const imgs = [star1, star2].map(src => {
      const i = new Image();
      i.src = src;
      return i.decode?.().catch(() => { }) ?? Promise.resolve();
    });
    // no cleanup needed
  }, []);

  // build stars when modal opens; clear when it closes
  useEffect(() => {
    if (!open) return;
    const starContainer = starRef.current;
    if (!starContainer) return;

    // clear first (React 18 strict + re-opens)
    starContainer.innerHTML = "";

    // Device-aware star count (trim for low DPR or low memory)
    const dpr = window.devicePixelRatio || 1;
    const base = 50;
    const starCount = dpr < 1.5 ? Math.floor(base * 0.6) : base;

    // Build in a single document fragment (less layout churn)
    const frag = document.createDocumentFragment();

    for (let i = 0; i < starCount; i++) {
      const star = document.createElement("img");
      star.src = Math.random() < 0.6 ? star1 : star2;
      star.alt = "";
      star.decoding = "async";
      star.loading = "eager"; // programmatic hint
      star.className = "animated-starm";

      // base position & size
      const px = Math.random() * 100;   // % left
      const py = Math.random() * 100;   // % top
      const size = 8 + Math.random() * 14;

      star.style.top = `${py}%`;
      star.style.left = `${px}%`;
      star.style.width = `${size}px`;
      star.style.height = `${size}px`;

      // rotation used by all keyframes
      star.style.setProperty("--rot", `${Math.floor(Math.random() * 360)}deg`);

      // entrance direction (burst from edges)
      const edge = Math.floor(Math.random() * 4); // 0=L,1=R,2=T,3=B
      const burstOffset = 148 + Math.random() * 48; // px
      let fromX = 0, fromY = 0;
      if (edge === 0) fromX = -burstOffset;      // left
      if (edge === 1) fromX = burstOffset;      // right
      if (edge === 2) fromY = -burstOffset;      // top
      if (edge === 3) fromY = burstOffset;      // bottom

      // small idle drift deltas
      star.style.setProperty("--x0", "0px");
      star.style.setProperty("--y0", "0px");
      star.style.setProperty("--dx", `${-6 + Math.random() * 12}px`);
      star.style.setProperty("--dy", `${-6 + Math.random() * 12}px`);

      // stagger the burst
      star.style.setProperty("--burstDelay", `${(Math.random() * 0.25).toFixed(2)}s`);
      star.style.setProperty("--burstDur", `${(0.35 + Math.random() * 0.35).toFixed(2)}s`);
      star.style.setProperty("--fromX", `${fromX}px`);
      star.style.setProperty("--fromY", `${fromY}px`);

      // timing vars for twinkle/drift
      star.style.setProperty("--twinkDur", `${(2 + Math.random() * 3).toFixed(2)}s`);
      star.style.setProperty("--floatDur", `${(3 + Math.random() * 4).toFixed(2)}s`);
      star.style.setProperty("--twinkDelay", `${(Math.random() * 3).toFixed(2)}s`);
      star.style.setProperty("--floatDelay", `${(Math.random() * 2).toFixed(2)}s`);

      frag.appendChild(star);
    }
    starContainer.appendChild(frag);

    return () => { starContainer.innerHTML = ""; };
  }, [open]);

  //  NEW: restart animations every hover/focus
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    const layer = starRef.current;
    if (!panel || !layer) return;

    const prefersReduced =
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

    const burstDurMs = 550; // little buffer over var(--burstDur)

    const restartStars = () => {
      if (prefersReduced) return; // respect reduced motion
      // start playing
      panel.classList.remove("stars-out");
      panel.classList.add("stars-play");
      // Stop all star animations on the next frame, then re-enable on the next
      layer.classList.add("is-restarting");
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          layer.classList.remove("is-restarting");
        });
      });
    };

    const burstOutStars = () => {
      panel.classList.remove("stars-play");
      panel.classList.add("stars-out");

      // after the out animation ends, clear the 'stars-out' state so we can play again later
      const timeout = setTimeout(() => {
        panel.classList.remove("stars-out");
      }, burstDurMs);

      // safety: if user re-enters quickly, cancel the pending cleanup
      const cancelOnEnter = () => clearTimeout(timeout);
      panel.addEventListener("mouseenter", cancelOnEnter, { once: true });
      panel.addEventListener("focusin", cancelOnEnter, { once: true });
    };

    const onFocusOut = (e) => {
      if (!panel.contains(e.relatedTarget)) burstOutStars();
    };

    panel.addEventListener("mouseenter", restartStars);
    panel.addEventListener("focusin", restartStars);
    panel.addEventListener("mouseleave", burstOutStars);
    panel.addEventListener("focusout", onFocusOut);

    return () => {
      panel.removeEventListener("mouseenter", restartStars);
      panel.removeEventListener("focusin", restartStars);
      panel.removeEventListener("mouseleave", burstOutStars);
      // focusout cleanup via same signature
      panel.removeEventListener("focusout", onFocusOut);
    };
  }, [open]);

  // Close on ESC
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Focus close for a11y
  useEffect(() => {
    if (open) closeBtnRef.current?.focus();
  }, [open]);

  const roles = Array.isArray(t.eligibility?.roles) ? t.eligibility.roles : ["DJs", "Producers", "Music Professionals"];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="backdrop"
          className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-[1px] overflow-y-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          aria-hidden="true"
        >
          {/* <div ref={starRef} className="relative w-[900px] h-[450px] left-[260px] top-[180px] pointer-events-none z-50 overflow-visible" /> */}
          {/* Panel */}
          <motion.div
            key="panel"
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="promo-modal-title"
            className="group rounded-[22.46px] overflow-hidden shadow-2xl absolute inset-x-0 top-[max(1rem,env(safe-area-inset-top))] 
                       mx-auto w-[92%] max-w-[648px] h-auto md:h-[292px]"
            initial={{ y: "-100%", opacity: 0.8 }}
            animate={{ y: 226, opacity: 1 }}
            exit={{ y: "-100%", opacity: 0 }}
            transition={{ type: "tween", duration: 0.22, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Ticket: mobile (stacked) vs desktop (label left) */}
            <div className="relative flex flex-col md:flex-row">
              <div className=" bg-[#02D1A2] text-white font-bold font-public-sans
                    flex items-center justify-center h-[65px]
                    md:h-[292px] md:w-[105px] md:[writing-mode:vertical-rl] md:rotate-180
                    md:leading-[42px]
                    px-4 py-3 md:px-0 md:py-0
                    text-[clamp(18px,3.2vw,28px)]
                    md:relative
                  "
                aria-hidden="true"
              >
                <span id="promo-modal-title">{t.label || "PROMO OFFER"}</span>

                {/* circular notch only on md+ */}
                <span
                  className="hidden md:block md:absolute md:-right-[28px] md:top-[42%]
                               md:w-[50px] md:h-[50px] md:rounded-full md:bg-[#000706]"
                  aria-hidden="true"
                />
              </div>

              {/* Ticket body */}
              <div className="flex-1 bg-[#000706] text-white px-5 md:px-[16.84px] py-6 md:py-[33.5px] relative overflow-visible">
                {/* star layer sits behind content */}
                <div
                  ref={starRef}
                  className="
                    star-layer absolute inset-[-36px]  
                    pointer-events-none z-0
                  "
                  aria-hidden="true"
                />

                <div className="relative z-10">
                  <p className="text-[clamp(18px,4vw,25.26px)] leading-[39.3px] font-bold  mb-4 break-words">
                    <span
                      className="
                      transition-all duration-0
                      group-hover:text-transparent
                      group-hover:bg-clip-text
                      group-hover:[background-image:linear-gradient(188.11deg,#4EEEE5_32.43%,#1871BF_72.94%)]
                    "
                    >
                      {couponCode || "HEAVYDISCOUNT"}
                    </span>
                  </p>

                  {couponDescription && (
                    <div className="relative z-20 font-manrope text-[17px] leading-[30.88px] tracking-[0%] text-[#838383] overflow-y-auto max-h-[180px]
                        bg-[#000706] rounded-[10px]  px-0 py-0 hide-scrollbar">
                      {couponDescription.split('\n').map((paragraph, index) => (
                        <p key={index} className="mb-3">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  )}

                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
